define([
	"dojo/_base/declare",
	"csi",
	"csi/action/EditController",
	"csi/reflection",
	"csi/types",
	"csi/xml",
	"src/c8-platform/type/uom",
	"src/c8-platform/type/value",
	"SiteAdmin/BusinessObject",
	"SiteAdmin/ConfigurableAttributeMixin",
	"SiteAdmin/Locale"
], function(declare, csi, EditController, reflection, types, xml, {getUOMConversionString}, {VALUE_TYPE}, BusinessObject, ConfigurableAttributeMixin, Locale) {

	return declare([EditController, ConfigurableAttributeMixin], {

		/**
		 * @override
		 * Allow controller to add parameters to popup view
		 */
		onCreatePopupParams: function(/*Object*/params) {
			// show UOM form if this attribute has a corresponding UOM conversion attribute
			if (this._isValueUOMAttribute()) {
				params.uomForm = true;
			}
		},

		onCreatePopup: function(/*csi.view.Widget*/view) {
			// summary: initialize custom attribute form with business object node
			var fd = this.getFormData();
			var node = this.getNode();
			var id = Locale.getConfigurableAttributeId(node);
			var type = Locale.getConfigurableAttributeType(node);
			const columnWidth = node[reflection.ATTR_COLUMN_WIDTH];
			const isDefaultWidth = !xml.enumGetText(columnWidth);

			if (node.Format && node.Format.indexOf("%") >= 0) {
				view.getField("Initial Value").heading.m_format = node.Format;
			}

			fd.setFieldInitialValue("Node Name", id);
			fd.setFieldInitialValue("LocaleName", Locale.getConfigurableAttributeNodeName(node));
			fd.setFieldInitialValue("LocaleTooltip", Locale.getTypeAttributeResource(type, id, Locale.TOOLTIP));
			fd.setFieldInitialValue("LocaleValidation", Locale.getTypeAttributeResource(type, id, Locale.ATTR_VALIDATION));

			// in the case of default, still populate the control to keep consistent
			if (isDefaultWidth) {
				fd.setFieldInitialValue(reflection.ATTR_COLUMN_WIDTH, this._getDefaultColumnWidth(node));
			}

			view.setBO(this.widget.getViewNode());

			this.inherited(arguments);
		},

		/**
		 * Calculate the column width by given attribute node
		 * @param {Node} node - the node of attribute
		 * @return {string}
		 */
		_getDefaultColumnWidth: function(node) {
			const id = Locale.getConfigurableAttributeId(node);
			const type = node[reflection.ATTR_VALUETYPE];
			const format = node[reflection.ATTR_FORMAT];

			const defaultWidthEnum = types.getAttributeDefaultColumnWidth(id, type, format);

			return defaultWidthEnum;
		},

		toWriter: function() {
			var fd = this.m_formData;
			var form = this.popupView;
			var node = form.getFormSourceNode();
			var bValidateValue = false;
			var cn = this.m_writer.changeNode(this.m_urlNode);

			var flags = this.getAttributeFlags(fd);
			if (flags != node[BusinessObject.ATTR_ATTRIBUTE_FLAGS]) {
				cn.changeAttribute(BusinessObject.ATTR_ATTRIBUTE_FLAGS, xml.ATTR_INT64, flags)
					.changeAttributeFlag(BusinessObject.ATTR_ATTRIBUTE_FLAGS, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			}

			// ExpressionOnClientDisabled flag is shown as evaluate on client enabled. So the value on client is the opposite of backend value
			if (fd.getFieldValue(BusinessObject.ATTR_EXP_ONCLIENT_ENABLED) == node[reflection.ATTR_EXP_ONCLIENT_DISABLED]) {
				cn.changeAttribute(reflection.ATTR_EXP_ONCLIENT_DISABLED, xml.ATTR_BOOLEAN, !fd.getFieldValue(BusinessObject.ATTR_EXP_ONCLIENT_ENABLED))
					.changeAttributeFlag(reflection.ATTR_EXP_ONCLIENT_DISABLED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			}

			// write new default type/value if changed
			var type = fd.getFieldValue(reflection.ATTR_VALUETYPE, true);
			var value = fd.getFieldValue(reflection.ATTR_VALUE, true) || xml.attributeTypeGetDefaultValue(type);

			// if the attribute doesn't have a dependsOn enum, clear out the DependsOn attribute on the node
			if (type === VALUE_TYPE.ENUM && !form.dependsOnEnumListName) {
				cn.changeAttribute(reflection.ATTR_DEPENDS_ON, xml.ATTR_REF, csi.EMPTY_URL);
			}

			// convert value to JSON appropriately
			value = this.convertInitialValueToJSON(type, value);

			if (fd.isFieldChanged(reflection.ATTR_VALUETYPE) ||
				fd.isFieldChanged(reflection.ATTR_VALUE) ||
				fd.isFieldChanged(BusinessObject.ATTR_TARGET_CLASS_NAME) ||
				(value !== node[reflection.ATTR_VALUE])
			) {
				cn.changeAttribute(reflection.ATTR_VALUETYPE, xml.ATTR_STRING, type)
					.changeAttributeFlag(reflection.ATTR_VALUETYPE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
					.changeAttribute(reflection.ATTR_VALUE, xml.ATTR_STRING, value)
					.changeAttributeFlag(reflection.ATTR_VALUE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);

				if (node.$Type == "CustomAttribute") {
					this.changeSubtypeAndFormat(cn, type);
				}

				bValidateValue = true;
			}

			this.applyBuildInAttributesCopyOption(fd, cn, form.getFormSourceNode());

			// DE21331: Manually mark CustomAttribute as Modified due to "Subtype" attribute lack of "Notify" constraint
			if (fd.isFieldChanged(reflection.ATTR_SUBTYPE)) {
				cn.changeAttribute(BusinessObject.ATTR_MODIFIED, xml.ATTR_BOOLEAN, true)
					.changeAttributeFlag(BusinessObject.ATTR_MODIFIED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			}

			this._handleColumnWidth(fd, true);

			// everything else can be set as usual
			this.inherited(arguments);

			// update display name for UOM conversion attribute
			if (this._isValueUOMAttribute()) {
				f = fd.getField("LocaleName");
				if (f && f.isUpdated()) {
					const typeClass = Locale.getConfigurableAttributeType(this.getNode());
					const id = getUOMConversionString(fd.getFieldValue(xml.ATTR_NAME));
					Locale.setResource(this.m_writer, Locale.ATTR_TYPES, [typeClass, Locale.TYPE_ATTR, id], f.getValue() + " UOM", null, true);
				}
			}

			// set localized name and tooltip
			this.toWriterLocale(bValidateValue || fd.isFieldChanged("ValidationExpression"));
		},

		/**
		 * Return true if this attribute has a corresponding UOM conversion attribute
		 * @return {boolean}
		 */
		_isValueUOMAttribute: function() {
			const conversionAttr = this.m_cache.getNode(getUOMConversionString(this.m_urlNode));
			return conversionAttr?.TargetClassName === "UOMConversion";
		}
	});
});